<template>
    <div class="tw:flex tw:flex-col tw:gap-5 tw:pb-4">

        <!-- ─── Resumen compacto del pago ─────────────────────────────── -->
        <div class="tw:rounded-lg tw:border tw:border-gray-200 tw:bg-white tw:overflow-hidden">
            <div class="tw:px-4 tw:py-3 tw:flex tw:items-center tw:justify-between">
                <span class="tw:text-sm tw:font-bold tw:text-gray-800">
                    {{ $t('perfil.cambio_asiento.total', 'Total a pagar') }}
                </span>
                <span class="tw:text-base tw:font-extrabold tw:text-gray-900">
                    {{ formatearPrecio(precioTotal) }}
                </span>
            </div>
            <div
                v-if="mostrarGastosGestion"
                class="tw:px-4 tw:py-2 tw:border-t tw:border-gray-100 tw:flex tw:items-center tw:justify-between"
            >
                <span class="tw:text-xs tw:text-gray-500">
                    {{ $t('perfil.cambio_asiento.gastos_gestion', 'Gastos de gestión') }}
                </span>
                <span class="tw:text-xs tw:text-gray-700">{{ formatearPrecio(priceManagement) }}</span>
            </div>
        </div>

        <!-- ─── Pago Paycomet (jet_id) — incluye Bizum heredado ────────── -->
        <!--
            Props requeridos por PagoPaycometComponent (idéntico a la
            nueva alta online ProcesoCompraAbonadoNormal.vue):
            - `store` y `get-vuex-alias`: para despachar la acción
              `front.procesar-pago-paycomet` tras el `jet_request`.
            - `importe-carrito`, `gastos-gestion-unitarios`, `num-abonados`:
              se propagan a FormularioPagoComponent / PaymentStep.
            - `es-plazos`: false (cambio-asiento no tiene plazos).
        -->
        <PagoPaycometComponent
            v-if="jetId"
            :jet_id="String(jetId)"
            :session_id="sessionId"
            :tipo_venta="'cambio-butaca'"
            :es-plazos="false"
            :importe-carrito="precioTotal"
            :gastos-gestion-unitarios="priceManagement"
            :num-abonados="1"
            :no-redirigir="true"
            :store="store"
            :get-vuex-alias="getVuexAlias"
            @payToken="onPayToken"
            @bizumError="onBizumError"
            @completado="onCompletado"
        />

        <!-- ─── Botón volver ────────────────────────────────────────── -->
        <Button variant="alt" @click="$emit('back')">
            <span class="tw:flex tw:items-center tw:gap-1">
                <i class="material-icons tw:text-base">arrow_back</i>
                {{ $t('perfil.cambio_asiento.atras', 'Atrás') }}
            </span>
        </Button>
    </div>
</template>

<script setup>
    import store from '@/store';
    import { config } from '@/config';
    import PagoPaycometComponent from '@/components/frontend/compra/components/PagoPaycometComponent.vue';
    import Button from '@/modules/front/ui/Button.vue';

    const getVuexAlias = (...args) => config.getVuexAlias(...args);

    /**
     * Paso de pago de cambio de asiento (PASO_PAGO).
     *
     * Se monta SOLO cuando el response de `reservar()` trae `jet_id` (pasarela
     * Paycomet). El componente padre condiciona el render a la presencia de
     * `jetId`; el TPV Redsys (`formulario`) se maneja desde el paso de
     * confirmación (CambioButacaPasoConfirmacion), que auto-submitea el form.
     *
     * - Paycomet (`jetId`): delega en PagoPaycometComponent (legacy Options
     *   API) que ya integra Bizum y la lógica de tokenización + 3DS challenge
     *   + polling. `no-redirigir=true` desactiva la redirección posterior al
     *   callback para quedarnos en el paso post-pago (PASO_PAGO_CONFIRMACION);
     *   el cierre del flujo lo hace el botón "Finalizar" de ese paso.
     */
    defineProps({
        /** Jet ID devuelto por el backend para iniciar el formulario Paycomet (con Bizum). */
        jetId: {
            type: [String, Number],
            default: null,
        },
        /** ID de sesión de reserva; se reenvía a `procesar-pago-paycomet`. */
        sessionId: {
            type: String,
            default: null,
        },
        /** Precio total a cobrar al usuario (importe_cambio + gastos_gestion). */
        precioTotal: {
            type: Number,
            default: 0,
        },
        /** Importe de los gastos de gestión ya sumados al `precioTotal`. */
        priceManagement: {
            type: Number,
            default: 0,
        },
        /** Cuando es true se renderiza la línea desglosada de gastos de gestión. */
        mostrarGastosGestion: {
            type: Boolean,
            default: false,
        },
    });

    const emit = defineEmits(['back', 'completado']);

    /**
     * PagoPaycometComponent gestiona internamente el submit y la navegación
     * posterior al callback (`/compra/:session_id/:n_pedido`). Aquí sólo
     * recibimos los handlers para integrarlos al ciclo del wizard.
     */
    function onPayToken() {
        // No-op: PagoPaycometComponent procesa el pago internamente vía Vuex.
    }

    function onBizumError(message) {
        // No-op: PagoPaycometComponent ya muestra el toast con el mensaje.
    }

    /**
     * PagoPaycometComponent emite `completado` cuando el pago se procesó
     * correctamente (respuesta de `get-pedido-procesado-paycomet` sin error).
     * Propagamos al padre para que emita `completado` al PerfilAbonosView
     * y muestre el modal de éxito.
     */
    function onCompletado(result) {
        emit('completado', result);
    }

    /** Da formato EUR a un número; defensivo ante strings no numéricos. */
    function formatearPrecio(val) {
        const n = parseFloat(val);
        if (isNaN(n)) return '—';
        return new Intl.NumberFormat('es-ES', {
            style: 'currency',
            currency: 'EUR',
        }).format(n);
    }
</script>
